import { DateTime } from 'luxon';

/**
 * The account's message figures, in one place (HORO-10534).
 *
 * Used by both tiers: the free allowance on Your Account, and a subscriber's
 * plan-plus-bundles balance on Plan Management. `limits.total` already spans
 * both pools, so one set of helpers serves both.
 *
 * Everything here reads what the backend already computed. `normalizeUserLimits`
 * does expiry-aware maths that the chat send-gate also relies on — in
 * particular it zeroes plan quota once `plan.validUntil` has passed, so a
 * lapsed plan reports no balance rather than one the gate would then reject.
 * Recomputing any of that here would mean two answers to the same question,
 * and the wrong one would be the one the customer reads.
 */

/**
 * Whether the account's plan is unlimited.
 *
 * HORO-10504 made `total` nullable to mean exactly that — `normalizeUserLimits`
 * returns `{ planTotal: null, total: null }` for the Subscriber tier. Note this
 * has to be an explicit null check: `!total` would read a genuine zero balance
 * as unlimited, which is the opposite of the truth and would hand a spent-out
 * account a meter reading full.
 */
export const isUnlimited = (limits) => limits?.total === null;

/**
 * Messages still spendable — the same expression the Subscription page's
 * "Messages available" counter uses, so the two can't disagree. Null when the
 * plan is unlimited: there is no countdown to show, and 0 would be a lie.
 *
 * Clamped at zero otherwise: `used` can legitimately exceed `total` after a
 * carry-over (HORO-10428), and a negative balance on screen is worse than a
 * floor. The defaults below deliberately don't cover null — `= 0` only applies
 * to undefined — hence the explicit check first.
 */
export const messagesRemaining = (limits) => {
    if (isUnlimited(limits)) return null;
    const { total = 0, used = 0 } = limits || {};
    return Math.max(0, total - used);
};

/**
 * Total the meter is measured against, or null when unlimited. `total` rather
 * than `planTotal` because the meter has to agree with the remaining count
 * above, and that count spans both pools — showing 30 remaining out of a
 * 20-message plan allowance would put the meter past full.
 */
export const messagesAllowance = (limits) => {
    if (isUnlimited(limits)) return null;
    return limits?.total || 0;
};

/** True once the customer holds bundle messages on top of the plan allowance. */
export const hasBundleMessages = (limits) => !isUnlimited(limits) && messagesAllowance(limits) > (limits?.planTotal || 0);

/**
 * Whether there is balance to spend on one more message (HORO-10679).
 *
 * The gate for the held message that sends itself after a pack is bought, so it
 * has to be quantitative rather than the `total === used` equality the send
 * paths use: a carry-over overshoot (`used > total`, HORO-10428) is not a
 * balance, and that equality would wave it through into a rejected send.
 * Unlimited is `total === null`, which covers someone who bought a subscription
 * rather than a pack.
 */
export const hasSendableCredit = (limits) => isUnlimited(limits) || messagesRemaining(limits) > 0;

/**
 * When the free allowance next refills.
 *
 * This is `plan.validUntil`, which is a real date for free users, not just
 * subscribers — the user schema defaults it to 30 days out, and the renewal
 * cron sweeps every account whose `validUntil` has passed (the filter is on the
 * date alone, deliberately not on `isFree`). For an account with no card that
 * sweep runs `makeUsersFree`, which zeroes `limits.plan.messages.used` and
 * pushes `validUntil` another 30 days. So the field already means "when the
 * allowance resets", and no new backend field is needed for it.
 *
 * Returns null when there's no usable date, and `isImminent` when the date has
 * passed — between expiry and the next cron pass `validUntil` sits in the past,
 * and "Resets on 20 Sept" printed three days after 20 Sept reads as a bug.
 */
export const allowanceReset = (plan, now = DateTime.now()) => {
    if (!plan?.validUntil) return null;

    const resetAt = DateTime.fromJSDate(new Date(plan.validUntil));
    if (!resetAt.isValid) return null;

    if (resetAt <= now) return { isImminent: true, label: null };

    return { isImminent: false, label: resetAt.toFormat('d LLL, yyyy') };
};
